import * as a from '@mitranim/js/all.mjs'
import * as dg from '@mitranim/js/dom_global'
import * as dr from '@mitranim/js/dom_reg.mjs'
import {A, E} from './util.mjs'
import * as c from './conf.mjs'
import * as em from './elem-misc.mjs'
import * as u from './util.mjs'

/*
Replacement for `https://github.com/vimeo/player.js`.
Writing this was easier than working around their shit.
*/
class VimeoPlayer extends em.Iframe {
  static {dr.reg(this)}

  /*
  Partially redundant with `MdRen..video` and `VimeoEmbed`,
  TODO dedup if possible.
  */
  init({id, autoplay, progress, controls, muted}) {
    a.reqValidStr(id)

    // Used for error messages.
    this.data().id = id

    return E(this, {
      class: `block full-sz abs-fit`,
      src: u.vimeoEmbedUrl({id, controls, muted}),
      allow: u.joinSemi(
        `encrypted-media`,
        a.vac(a.laxBool(autoplay)) && `autoplay`,
        a.vac(a.laxBool(controls)) && `fullscreen`,
        a.vac(a.laxBool(controls)) && `picture-in-picture`,
      ),
      frameborder: `0`,
      'data-progress': u.markerStr(progress),
    })
  }

  // Allows to track iframe initialization.
  #fut = undefined
  get task() {return (this.#fut ??= new u.Fut()).task}

  taskClear() {
    this.#fut?.reject(new a.AbortError())
    this.#fut = undefined
  }

  disconnectedCallback() {
    this.taskClear()
    super.disconnectedCallback()
  }

  onMessage(eve) {
    /*
    Note: Vimeo events sometimes have JSON-encoded data, and sometimes have
    regular structured data. We have to be flexible.
    */
    const data = u.maybeJsonDecode(eve.data)

    if (data?.event === `ready`) {
      this.onReady()
      return
    }

    if (data?.event === `error` && data?.data?.method === `ready`) {
      this.onReadyErr(data.data)
      return
    }

    if (data?.event === `timeupdate`) {
      this.onTimeUpdate(data.data)
      return
    }

    // TODO remove.
    u.logDebug(`unknown data in Vimeo iframe message:`, data)
  }

  onReady() {
    this.#fut?.resolve()
    this.play()
    if (`progress` in this.dataset) this.addTimeUpdate()
  }

  /*
  Including the video id into the error message may allow us to investigate
  specific videos when the error is reported by Sentry, particularly when
  the video is not found (which actually occurred). Without the id, it's hard
  to identify which video had caused an error.
  */
  onReadyErr(src) {
    const id = this.dataset.id
    const base = `error in Vimeo video ` + a.show(id)
    const err = Error(src?.message ? (base + `: ` + src.message) : base)
    const name = src?.name
    if (name) err.name = name
    this.#fut?.reject(err)
  }

  /*
  Example data:

    {
      duration: 358.998,
      percent: 0.005,
      seconds: 1.868,
    }

  Example handling on ancestor:

    this.onprogress = this.onProgress
  */
  onTimeUpdate(data) {u.dispatch(this, `progress`, data)}

  send(method, value) {
    a.reqValidStr(method)
    this.postMessage({method: a.reqValidStr(method), value})
  }

  play() {this.send(`play`)}
  pause() {this.send(`pause`)}
  setCurrentTime(val) {this.send(`setCurrentTime`, val)}
  rewind() {this.setCurrentTime(0)}
  replay() {this.rewind(), this.play()}
  addEvent(name) {this.send(`addEventListener`, a.reqValidStr(name))}
  addTimeUpdate() {this.addEvent(`timeupdate`)}
}

export class VimeoVideo extends u.Elem {
  static {dr.reg(this)}

  init({videoId, previewImageUrl, hover, autoplay = hover}) {
    return E(this, A
      .cls(`vimeo-video`)
      .cls(!hover && `cur-point`)
      .dataset({
        videoId,
        previewImageUrl,
        hover: u.markerStr(hover),
        autoplay: u.markerStr(autoplay),
      })
    )
  }

  chiImg() {
    return E(this, {}, new VideoImg().init(this.dataset.previewImageUrl))
  }

  chiVid() {
    if (this.shouldDisable()) return this.chiImg()

    return E(this, {},
      new VimeoPlayer().init({
        id: this.dataset.videoId,
        progress: true,
        controls: !this.hover(),
        muted: this.hover(),
        autoplay: this.autoplay(),
      }),
      new VimeoVideoProgressBar().init(),
    )
  }

  #obs = undefined
  obs() {return this.#obs ??= new u.TaskObsLog()}

  player() {return this.desc(VimeoPlayer)}
  progressBar() {return this.desc(VimeoVideoProgressBar)}
  img() {return this.desc(VideoImg)}
  hover() {return `hover` in this.dataset}
  autoplay() {return `autoplay` in this.dataset}

  connectedCallback() {this.listen()}
  disconnectedCallback() {this.unlisten()}

  listen() {
    if (this.shouldDisable()) return

    if (this.hover()) {
      this.onmouseenter = this.onMouseEnter
      this.onmouseleave = this.onMouseLeave
    }
    else {
      this.onclick = this.onClick
    }
    this.onprogress = this.onProgress
  }

  unlisten() {
    this.onmouseenter = null
    this.onmouseleave = null
    this.onclick = null
    this.onprogress = null
  }

  onMouseEnter(eve) {a.eventKill(eve), this.restart()}
  onMouseLeave(eve) {a.eventKill(eve), this.stop()}
  onClick(eve) {a.eventKill(eve), this.restart()}
  onProgress(eve) {this.progressBar()?.setPercent(eve?.detail?.percent)}
  restart() {this.obs().idle()?.setTask(this.restartAsync())}

  async restartAsync() {
    this.stop()

    /*
    Note: when using mouseenter/mouseleave, we MUST remove event listeners just
    before adding the iframe, and restore them asynchronously after adding the
    iframe. Without this precaution, the mere act of adding an iframe causes
    fake mouseenter/mouseleave events, which can trigger a continuous loop of
    initialization/deinitialization, breaking the functionality and wasting
    tons of CPU, network, and battery.
    */
    this.unlisten()
    try {this.chiVid()}
    finally {
      await u.requestAnimationFramePromise()
      this.listen()
    }
  }

  stop() {if (this.player()) this.chiImg()}
  shouldDisable() {return !c.EXTERNAL_INTEGRATIONS_ENABLED || c.DEV_DISABLE_IFRAMES}
}

class VideoImg extends u.MixElem(dg.global.HTMLImageElement) {
  static {dr.reg(this)}

  init(src) {return E(this, A.cls(`video-preview-img`).src(src))}

  /*
  Known deficiency: this may not work when loading a new page from SSR, if the
  image fails to load before we initialize the custom element class. In
  addition to attaching the event listener, we should check if the image has
  already failed to load (how?). TODO find if we can provide a fallback image
  by using special native attributes. Reference:

    https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img
  */
  connectedCallback() {this.onerror = this.onError}

  onError() {this.src = a.laxStr(c.IMAGE_PLACEHOLDER)}
}

class VimeoVideoProgressBar extends u.Elem {
  static {dr.reg(this)}
  init() {return E(this, A.cls(`vimeo-video-progress-bar`))}
  setPercent(val) {return E(this, A.style({width: u.formatPercentage(val)}))}
}
